import { ErrorModal } from "@dexteel/mesf-core";
import AddIcon from "@mui/icons-material/Add";
import {
	Box,
	Button,
	Dialog,
	DialogActions,
	DialogContent,
	DialogContentText,
	DialogTitle,
	Paper,
	Typography,
} from "@mui/material";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
	Group,
	Panel,
	Separator,
	useDefaultLayout,
} from "react-resizable-panels";
import { CenteredLazyLoading } from "@/controls/LazyLoading";
import { NotificationSnackBar } from "@/controls/snackbars/notification-snackbar";
import {
	urlParam,
	useDropUnknownValue,
	useUrlState,
	useUrlText,
} from "@/shared/hooks/useUrlState";
import { CREWS } from "../../straightener-common/constants";
import { useStraightenerCanEdit } from "../../straightener-common/permissions";
import { RecipeEditorPanel } from "../../straightener-common/components/RecipeEditorPanel";
import { UnsavedChangesModal } from "../../straightener-common/components/UnsavedChangesModal";
import { buildProductNodes } from "../../straightener-common/productNodes";
import {
	useStraightenerRecipeEditor,
	withoutBand,
} from "../../straightener-common/hooks/useStraightenerRecipeEditor";
import {
	useCurrentCrew,
	useRemoveStraightenerRecipe,
	useStraightenerProducts,
	useStraightenerRecipes,
} from "../hooks/useStraightenerRecipes";
import type {
	ProductNode,
	StraightenerProduct,
	StraightenerRecipeVersion,
} from "../models/StraightenerRecipe";
import { getStraightenerRecipeValues } from "../repositories/StraightenerRecipesRepository";
import { DuplicateStraightenerRecipeModal } from "./DuplicateStraightenerRecipeModal";
import { AddRecipeModal, type StartFromMode } from "./AddRecipeModal";
import { StraightenerProductsTree } from "./StraightenerProductsTree";
import { StraightenerRecipesToolbar } from "./StraightenerRecipesToolbar";

// The tree only lists a product and its versions; the editor carries the field
// cards and the whole rolls table, so the width belongs on the right. At 30 the
// field grid gets a fifth column and the rolls table still fits unscrolled.
const DEFAULT_LAYOUT = { left: 30, right: 70 };

const URL_SCHEMA = {
	search: urlParam.string(),
	productType: urlParam.string(),
	crew: urlParam.oneOf(CREWS),
	withRecipe: urlParam.bool(false),
	productId: urlParam.int(),
	recipeId: urlParam.int(),
};

export const StraightenerRecipesManager = () => {
	// Filters and the product and recipe asked for, in the query string.
	const [
		{
			search,
			productType: productTypeParam,
			crew: crewParam,
			withRecipe,
			productId,
			recipeId,
		},
		setFilters,
		{ dropValues },
	] = useUrlState(URL_SCHEMA);
	// The box types locally and the URL follows once typing stops.
	const [searchText, setSearchText] = useUrlText(search ?? "", (value) =>
		setFilters({ search: value || null }),
	);
	const productType = productTypeParam ?? "";
	const crew = crewParam ?? "";
	const [selectedProductId, setSelectedProductId] = useState<number | null>(
		null,
	);
	const [selectedRecipeId, setSelectedRecipeId] = useState<number | null>(null);
	const [editProduct, setEditProduct] = useState<StraightenerProduct | null>(
		null,
	);
	// A product with no recipe that was selected: the right panel shows an empty
	// state with a "Create recipe" CTA instead of jumping straight into the form.
	const [emptyProduct, setEmptyProduct] = useState<StraightenerProduct | null>(
		null,
	);
	const [addOpen, setAddOpen] = useState(false);
	// When set, the Add Recipe modal opens with the product fixed (from a
	// product's empty state); null = the toolbar ADD RECIPE flow (product free).
	const [lockProductId, setLockProductId] = useState<number | null>(null);
	// Unsaved-changes guard for tree navigation: the editor reports its dirty
	// state, and a click on another recipe/product is held until confirmed.
	const [editorDirty, setEditorDirty] = useState(false);
	const [pendingSelect, setPendingSelect] = useState<(() => void) | null>(null);
	// Version pending the delete confirmation (soft delete: it disappears from
	// every screen; the name is freed for reuse).
	const [removeTarget, setRemoveTarget] =
		useState<StraightenerRecipeVersion | null>(null);
	// Prefilled name + title variant for a duplicate-in-progress (client-side:
	// nothing is persisted until SAVE).
	const [newRecipeName, setNewRecipeName] = useState("");
	const [newRecipeTitle, setNewRecipeTitle] = useState<string | undefined>(
		undefined,
	);
	const canEdit = useStraightenerCanEdit();

	const guardNavigation = useCallback(
		(action: () => void) => {
			if (editorDirty) setPendingSelect(() => action);
			else action();
		},
		[editorDirty],
	);

	const { defaultLayout, onLayoutChange } = useDefaultLayout({
		id: "straightener-recipes-v1",
		panelIds: ["left", "right"],
		storage: localStorage,
	});

	const productsQ = useStraightenerProducts();
	const recipesQ = useStraightenerRecipes();
	const crewQ = useCurrentCrew();

	const editor = useStraightenerRecipeEditor({
		onSavedNew: (newId) => setSelectedRecipeId(newId),
	});

	const { removeRecipe, isRemoving } = useRemoveStraightenerRecipe({
		onSuccess: () => editor.setNotification("Recipe deleted"),
		onError: (e) => editor.setError(e.message),
	});
	const confirmRemove = () => {
		if (!removeTarget) return;
		const id = removeTarget.Id;
		setRemoveTarget(null);
		if (selectedRecipeId === id) setSelectedRecipeId(null);
		removeRecipe(id);
	};

	const selectedVersion = useMemo(
		() => (recipesQ.data ?? []).find((r) => r.Id === selectedRecipeId) ?? null,
		[recipesQ.data, selectedRecipeId],
	);

	const productTypes = useMemo(() => {
		const set = new Set<string>();
		for (const p of productsQ.data ?? []) {
			if (p.ProductTypeName) set.add(p.ProductTypeName);
		}
		return Array.from(set).sort();
	}, [productsQ.data]);

	// A link to a product type the catalog no longer has falls back to all of them.
	useDropUnknownValue(
		productTypeParam,
		productsQ.isSuccess,
		productTypes,
		useCallback(() => dropValues("productType"), [dropValues]),
	);

	// All products with their current recipe (unfiltered) — used as copy sources.
	const allNodes = useMemo<ProductNode[]>(
		() => buildProductNodes(productsQ.data ?? [], recipesQ.data ?? []),
		[productsQ.data, recipesQ.data],
	);

	// The left tree: allNodes with the crew filter applied to versions, plus the
	// product-type / search filters.
	const productNodes = useMemo<ProductNode[]>(() => {
		// Split on blanks and parentheses and require every term. The tree labels a
		// product as "(Type) Name", and that whole label gets copied back into the
		// box — as one literal string it matches neither field on its own, since
		// the parentheses and the type live outside the product name.
		const terms = searchText
			.toLowerCase()
			.split(/[\s()]+/)
			.filter(Boolean);
		const matchesAll = (haystack: string) =>
			terms.every((t) => haystack.includes(t));
		const productMatches = (n: ProductNode) =>
			matchesAll(`${n.productTypeName} ${n.productName}`.toLowerCase());
		return allNodes
			.map((n) => {
				// Crew filter narrows the versions (and recomputes the current).
				let node = n;
				if (crew) {
					const versions = n.versions.filter((v) => v.Crew === crew);
					node = {
						...n,
						versions,
						current: versions.find((v) => v.IsCurrent) ?? null,
					};
				}
				// A search that matched a version name (not the product) narrows the
				// listed versions to the matching ones; the product's current pointer
				// is left intact for the summary and the product-row click.
				if (terms.length > 0 && !productMatches(node)) {
					node = {
						...node,
						versions: node.versions.filter(
							(v) => !!v.Name && matchesAll(v.Name.toLowerCase()),
						),
					};
				}
				return node;
			})
			.filter((n) => {
				if (productType && n.productTypeName !== productType) return false;
				if (withRecipe && n.versions.length === 0) return false;
				if (terms.length === 0) return true;
				return productMatches(n) || n.versions.length > 0;
			});
	}, [allNodes, searchText, productType, crew, withRecipe]);

	// Names of the selected product's other recipes (lower-cased) for the
	// unique-per-product check, excluding the recipe currently being edited.
	const existingRecipeNames = useMemo<string[]>(() => {
		if (selectedProductId == null) return [];
		const node = allNodes.find((n) => n.productSizeId === selectedProductId);
		if (!node) return [];
		return node.versions
			.filter((v) => v.Id !== selectedRecipeId)
			.map((v) => (v.Name ?? "").trim().toLowerCase())
			.filter(Boolean);
	}, [allNodes, selectedProductId, selectedRecipeId]);

	// Names already taken on the product being duplicated from. The copy is a new
	// recipe of that same product, so every existing version counts — including
	// the source, whose name the copy cannot reuse.
	const duplicateExistingNames = useMemo<string[]>(() => {
		const src = editor.recipeToDuplicate;
		if (!src) return [];
		const node = allNodes.find((n) => n.productSizeId === src.ProductSizeId);
		return (node?.versions ?? [])
			.map((v) => (v.Name ?? "").trim().toLowerCase())
			.filter(Boolean);
	}, [allNodes, editor.recipeToDuplicate]);

	// Whether the product shown in the empty state already has recipe versions
	// (tunes the CTA copy: "no recipe yet" vs "create another").
	const emptyProductHasRecipes = useMemo(
		() =>
			emptyProduct
				? (allNodes.find((n) => n.productSizeId === emptyProduct.Id)?.versions
						.length ?? 0) > 0
				: false,
		[allNodes, emptyProduct],
	);

	// Resolve the full product (with Dim1/2/3) from the catalog; fall back to the
	// slimmer object when not loaded yet.
	const findProduct = useCallback(
		(id: number, fallback: StraightenerProduct): StraightenerProduct =>
			(productsQ.data ?? []).find((p) => p.Id === id) ?? fallback,
		[productsQ.data],
	);

	const selectVersion = useCallback(
		(v: StraightenerRecipeVersion) => {
			setEmptyProduct(null);
			setNewRecipeName("");
			setNewRecipeTitle(undefined);
			setSelectedProductId(v.ProductSizeId);
			setEditProduct(
				findProduct(v.ProductSizeId, {
					Id: v.ProductSizeId,
					Name: v.ProductName,
					ProductTypeName: v.ProductTypeName ?? "",
				}),
			);
			setSelectedRecipeId(v.Id);
		},
		[findProduct],
	);

	const handleSelectProduct = useCallback(
		(node: ProductNode) => {
			// Selecting a product means "create a new recipe for it" — show the
			// empty state with a Create CTA (never auto-open its current recipe;
			// versions are edited by clicking their own row). The editor unmounts,
			// so clear the dirty flag it can no longer report.
			setEditorDirty(false);
			setNewRecipeName("");
			setNewRecipeTitle(undefined);
			setSelectedProductId(node.productSizeId);
			setSelectedRecipeId(null);
			setEditProduct(null);
			setEmptyProduct(
				findProduct(node.productSizeId, {
					Id: node.productSizeId,
					Name: node.productName,
					ProductTypeName: node.productTypeName,
				}),
			);
		},
		[findProduct],
	);

	const handleClose = useCallback(() => {
		setEditorDirty(false);
		setSelectedProductId(null);
		setSelectedRecipeId(null);
		setEditProduct(null);
		setEmptyProduct(null);
		setNewRecipeName("");
		setNewRecipeTitle(undefined);
	}, []);

	// The URL carries the product and recipe; the panel keeps its own state, because a
	// recipe being created has no id yet. The two differ only while unsaved edits hold a
	// change that did not start with a click here (the sidebar link, or Back to it), so
	// the URL cannot discard them behind the guard's back.
	const ready = productsQ.isSuccess && recipesQ.isSuccess;
	const applied = `${selectedProductId}|${selectedRecipeId}`;
	const asked = `${productId}|${recipeId}`;
	const hydrated = useRef(false);

	const applyFromUrl = (pid: number | null, rid: number | null) => {
		const version =
			rid === null
				? undefined
				: (recipesQ.data ?? []).find((r) => r.Id === rid);
		if (version) {
			selectVersion(version);
			return;
		}
		const node =
			pid === null ? undefined : allNodes.find((n) => n.productSizeId === pid);
		// A product with no recipe asked for is the empty state with its Create CTA.
		if (node) handleSelectProduct(node);
		else handleClose();
		// Whatever the link asked for and no longer exists leaves the URL.
		const unknown: ("productId" | "recipeId")[] = [];
		if (rid !== null) unknown.push("recipeId");
		if (pid !== null && !node) unknown.push("productId");
		if (unknown.length) dropValues(...unknown);
	};

	// biome-ignore lint/correctness/useExhaustiveDependencies: only the URL or the first load starts this
	useEffect(() => {
		if (!ready) return;
		hydrated.current = true;
		if (asked === applied) return;
		if (editorDirty)
			setPendingSelect(() => () => applyFromUrl(productId, recipeId));
		else applyFromUrl(productId, recipeId);
	}, [ready, asked]);

	// A click here, a save or a delete moved the panel: the URL follows.
	// biome-ignore lint/correctness/useExhaustiveDependencies: only the panel starts this
	useEffect(() => {
		if (!hydrated.current || asked === applied) return;
		setFilters({ productId: selectedProductId, recipeId: selectedRecipeId });
	}, [applied]);

	// Add Recipe modal → open a NEW recipe for the chosen product, pre-filled
	// according to the selected "Start from" source.
	const handleCreateRecipe = useCallback(
		async (args: {
			productId: number;
			mode: StartFromMode;
			sourceRecipeId: number | null;
		}) => {
			const p = (productsQ.data ?? []).find((x) => x.Id === args.productId);
			if (!p) return;
			setEmptyProduct(null);
			setNewRecipeName("");
			setNewRecipeTitle(undefined);
			setSelectedProductId(p.Id);
			// Pass the full catalog product: the editor auto-fills the Product
			// Dimensions from Dim1/2/3.
			setEditProduct(p);
			setSelectedRecipeId(null);
			if (args.mode === "blank") editor.setNewRecipeValues([]);
			else if (args.mode === "current") await editor.prefillFromCurrentLine();
			else if (args.sourceRecipeId) {
				try {
					editor.setNewRecipeValues(
						withoutBand(await getStraightenerRecipeValues(args.sourceRecipeId)),
					);
				} catch (e) {
					editor.setError((e as Error).message);
				}
			}
			setAddOpen(false);
		},
		[
			productsQ.data,
			editor.setNewRecipeValues,
			editor.prefillFromCurrentLine,
			editor.setError,
		],
	);

	useEffect(() => {
		if (recipesQ.error) editor.setError(recipesQ.error.message);
		else if (productsQ.error) editor.setError(productsQ.error.message);
	}, [recipesQ.error, productsQ.error, editor.setError]);

	const isLoading = productsQ.isLoading || recipesQ.isLoading;

	return (
		<Box
			sx={{
				width: "100%",
				height: "calc(100vh - 130px)",
				display: "flex",
				flexDirection: "column",
				p: 1,
			}}
		>
			<Typography variant="h5" sx={{ fontWeight: 600, mb: 1 }}>
				Straightener Recipes
			</Typography>

			<Paper
				elevation={3}
				sx={{
					flex: 1,
					minHeight: 0,
					display: "flex",
					flexDirection: "column",
					p: 2,
				}}
			>
				<StraightenerRecipesToolbar
					search={searchText}
					onSearchChange={setSearchText}
					productType={productType}
					onProductTypeChange={(value) =>
						setFilters({ productType: value || null })
					}
					productTypes={productTypes}
					crew={crew}
					onCrewChange={(value) =>
						setFilters({ crew: CREWS.find((c) => c === value) ?? null })
					}
					withRecipe={withRecipe}
					onWithRecipeChange={(value) => setFilters({ withRecipe: value })}
					onRefresh={() => {
						productsQ.refetch();
						recipesQ.refetch();
					}}
					isRefreshing={productsQ.isFetching || recipesQ.isFetching}
					onAdd={() =>
						guardNavigation(() => {
							setLockProductId(null);
							setAddOpen(true);
						})
					}
					canAdd={canEdit}
				/>

				<Group
					id="straightener-recipes-group"
					orientation="horizontal"
					defaultLayout={defaultLayout ?? DEFAULT_LAYOUT}
					onLayoutChange={onLayoutChange}
					style={{ flex: 1, width: "100%", minHeight: 0 }}
				>
					<Panel id="left" minSize={25} style={{ paddingBottom: 5 }}>
						<Box
							sx={{
								display: "flex",
								flexDirection: "column",
								height: "100%",
								pr: 1,
							}}
						>
							<Paper
								variant="outlined"
								sx={{ flex: 1, width: "100%", overflow: "hidden" }}
							>
								{isLoading ? (
									<CenteredLazyLoading />
								) : (
									<StraightenerProductsTree
										nodes={productNodes}
										selectedProductId={selectedProductId}
										selectedRecipeId={selectedRecipeId}
										onSelectProduct={(node) =>
											guardNavigation(() => handleSelectProduct(node))
										}
										onSelectVersion={(v) =>
											v.Id === selectedRecipeId
												? selectVersion(v)
												: guardNavigation(() => selectVersion(v))
										}
										onRemoveVersion={canEdit ? setRemoveTarget : undefined}
										onDuplicateVersion={
											canEdit ? editor.setRecipeToDuplicate : undefined
										}
										onCreateRecipe={
											canEdit
												? (node) =>
														guardNavigation(() => {
															setLockProductId(node.productSizeId);
															setAddOpen(true);
														})
												: undefined
										}
									/>
								)}
							</Paper>
						</Box>
					</Panel>

					<Separator
						style={{
							display: "flex",
							alignItems: "center",
							justifyContent: "center",
							cursor: "col-resize",
						}}
					>
						<Box
							sx={{
								width: 6,
								height: 40,
								borderRadius: 3,
								backgroundColor: "divider",
								"&:hover": { backgroundColor: "primary.main" },
							}}
						/>
					</Separator>

					<Panel id="right" minSize={30} style={{ paddingBottom: 5 }}>
						<Box sx={{ pl: 1, height: "100%" }}>
							<Paper
								variant="outlined"
								sx={{ height: "100%", overflow: "hidden" }}
							>
								{emptyProduct ? (
									<Box
										sx={{
											height: "100%",
											display: "flex",
											flexDirection: "column",
											alignItems: "center",
											justifyContent: "center",
											gap: 2,
											p: 3,
											textAlign: "center",
										}}
									>
										<Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
											({emptyProduct.ProductTypeName}) {emptyProduct.Name}
										</Typography>
										<Typography color="text.secondary">
											{emptyProductHasRecipes
												? "Select a version on the left to edit it, or create a new recipe."
												: "Create the first recipe for this product."}
										</Typography>
										{canEdit ? (
											<Button
												variant="contained"
												startIcon={<AddIcon />}
												onClick={() => {
													setLockProductId(emptyProduct.Id);
													setAddOpen(true);
												}}
											>
												Create recipe
											</Button>
										) : null}
									</Box>
								) : (
									<RecipeEditorPanel
										product={editProduct}
										recipeId={selectedRecipeId}
										version={selectedVersion?.Version ?? null}
										recipeName={selectedVersion?.Name ?? newRecipeName}
										selectedVersion={selectedVersion}
										allNodes={allNodes}
										existingNames={existingRecipeNames}
										defaultCrew={crewQ.data ?? ""}
										editor={editor}
										showPrefill={false}
										readOnly={!canEdit}
										onDirtyChange={setEditorDirty}
										newRecipeTitle={newRecipeTitle}
										onClose={handleClose}
									/>
								)}
							</Paper>
						</Box>
					</Panel>
				</Group>
			</Paper>

			<DuplicateStraightenerRecipeModal
				open={!!editor.recipeToDuplicate}
				recipe={editor.recipeToDuplicate}
				existingNames={duplicateExistingNames}
				onDuplicate={async (id, name) => {
					// Client-side duplicate: open a prefilled NEW recipe; nothing is
					// written until the user hits SAVE.
					const src = editor.recipeToDuplicate;
					editor.setRecipeToDuplicate(null);
					if (!src) return;
					const p = (productsQ.data ?? []).find(
						(x) => x.Id === src.ProductSizeId,
					);
					if (!p) return;
					setEmptyProduct(null);
					setSelectedProductId(p.Id);
					setEditProduct(p);
					setSelectedRecipeId(null);
					setNewRecipeName((name ?? src.Name ?? "").trim());
					setNewRecipeTitle("Duplicate Recipe");
					try {
						editor.setNewRecipeValues(await getStraightenerRecipeValues(id));
					} catch (e) {
						editor.setError((e as Error).message);
					}
				}}
				onCancel={() => editor.setRecipeToDuplicate(null)}
			/>

			<AddRecipeModal
				open={addOpen}
				products={productsQ.data ?? []}
				nodes={allNodes}
				defaultProductId={lockProductId ?? selectedProductId}
				defaultSourceRecipeId={selectedRecipeId}
				productLocked={lockProductId != null}
				onCreate={handleCreateRecipe}
				onCancel={() => setAddOpen(false)}
			/>

			<UnsavedChangesModal
				open={pendingSelect != null}
				onKeepEditing={() => {
					setPendingSelect(null);
					// The URL moved on its own: point it back at what is still being edited.
					if (asked !== applied)
						setFilters({
							productId: selectedProductId,
							recipeId: selectedRecipeId,
						});
				}}
				onDiscard={() => {
					const action = pendingSelect;
					setPendingSelect(null);
					action?.();
				}}
			/>

			<Dialog open={removeTarget != null} onClose={() => setRemoveTarget(null)}>
				<DialogTitle>
					{removeTarget?.IsCurrent
						? "Delete the CURRENT recipe?"
						: "Delete recipe?"}
				</DialogTitle>
				<DialogContent>
					<DialogContentText>
						{removeTarget
							? removeTarget.IsCurrent
								? `This is the default recipe for ${removeTarget.ProductName} — "${removeTarget.Name}" (v${removeTarget.Version}) will be deleted and disappear from every screen. Are you sure?`
								: `"${removeTarget.Name}" (v${removeTarget.Version}) will be deleted and disappear from every screen. Are you sure?`
							: ""}
					</DialogContentText>
				</DialogContent>
				<DialogActions>
					<Button onClick={() => setRemoveTarget(null)} disabled={isRemoving}>
						CANCEL
					</Button>
					<Button
						color="error"
						variant="contained"
						onClick={confirmRemove}
						disabled={isRemoving}
					>
						DELETE
					</Button>
				</DialogActions>
			</Dialog>

			<NotificationSnackBar
				message={editor.notification}
				onHide={() => editor.setNotification("")}
			/>
			<ErrorModal error={editor.error} onHide={() => editor.setError("")} />
		</Box>
	);
};
